import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Shield, Truck, Leaf, Star, Quote, MoveRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { ProductCard } from '../../components/shared/ProductCard';
import { productService } from '../../services/productService';
import { ProductGridSkeleton } from '../../components/shared/Skeletons';
import { toast } from 'sonner';

/* ── animations ── */
const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.4, 0, 0.2, 1] } }
};
const stagger = { visible: { transition: { staggerChildren: 0.13 } } };

/* ── marquee items ── */
const MARQUEE_ITEMS = [
  'Sjenički Kajmak', 'Domaći maslac', 'Sjenički Sir', 'Suvo Meso',
  'Sudžuk', 'Kiselo Mleko', 'Jogurt', 'Masne paprike', "Paprike u pavlaci",
  'Kupus u pavlaci',
];

const ADVANTAGES = [
  { icon: Leaf,   title: '100% Prirodno',       desc: 'Birani proizvodi iz sjeničkog kraja, bez kompromisa u kvalitetu.' },
  { icon: Shield, title: 'Proveren kvalitet', desc: 'Svakodnevno vodimo računa o svežini, ukusu i poreklu svake namirnice.' },
  { icon: Truck,  title: 'Brza dostava',    desc: 'Pouzdana dostava na kućnu adresu ili lično preuzimanje u radnji.' },
  { icon: Star,   title: 'Porodična tradicija', desc: 'Godinama gradimo poverenje kupaca kroz domaće proizvode i uslugu.' },
];

const TESTIMONIALS = [
  {
    name: 'Saša Janković, Leskovac',
    text: 'Kupio sam sir ovčiji-kravlji, maslac, kajmak, sudžuk, pršutu i dobili na poklon papriku u pavlaci. Kvalitet, ne postoji bolje. Usluga, nema ljubaznijih i srčanijih. Cena, da li je moguće da je ovoliko jeftino? Eto planiramo odlazak na more za Grčku ali SAMO zbog ove radnje, zbog ovih ljudi mislim da ćemo za Crnu Goru samo da bih svratio kod ovih ljudi i pošteno im stisnuo ruku. I pod ovim nebeskim svodom postoje ovakvi neverovatni ljudi koji su ljudi pre svega. Naklon do poda porodici Fazlić.',
    stars: 5,
    featured: true
  },
  { name: 'A. H., Prijepolje', text: 'Kupujem redovno već duže vreme. Kvalitet je uvek isti, a usluga veoma ljubazna.', stars: 5 },
  { name: 'J. P., okolina Prijepolja', text: 'Odličan izbor sjeničkih proizvoda. Sve preporuke za kvalitet i pouzdanost.', stars: 5 },
  { name: 'N. S., Novi Pazar', text: 'Poručujem više puta godišnje. Uvek isti kvalitet i odličan ukus proizvoda.', stars: 5 },
  { name: 'I. K., Beograd', text: 'Sve je stiglo sveže i tačno kako je opisano. Posebno preporučujem kajmak.', stars: 5 },
  { name: 'B. R., Prijepolje', text: 'Maslac, sir i kiselo mleko su vrhunskog kvaliteta. Usluga je profesionalna i brza, sve preporuke.', stars: 5 },
  { name: 'M. T., Sjenica', text: 'Konačno prava domaća roba i ukus koji podseća na detinjstvo. Poručivanje jednostavno, isporuka uredna.', stars: 5 },
  { name: 'V. L., Užice', text: 'Naručili smo više proizvoda i svi su bili sveži i odlično upakovani. Posebno izdvajam kajmak i sudžuk.', stars: 5 },
  { name: 'D. P., Kragujevac', text: 'Kupujem kad god sam u prolazu i kvalitet je uvek isti. Ljubaznost i odnos prema kupcu su na visokom nivou.', stars: 5 },
];

/* ── Ticker/Marquee (kompaktno za ~10% visine prvog ekrana) ── */
const Marquee = () => (
  <div className="relative flex h-full min-h-0 items-center overflow-hidden bg-white">
    <motion.div
      animate={{ x: ['0%', '-50%'] }}
      transition={{ duration: 28, repeat: Infinity, ease: 'linear' }}
      className="flex w-max gap-0 whitespace-nowrap"
    >
      {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
        <span
          key={i}
          className="flex items-center gap-2 px-3 font-serif tracking-wide text-primary sm:gap-2.5 sm:px-4 md:px-5 text-[11px] sm:text-xs md:text-sm"
        >
          {item}
          <span className="text-primary/45 text-xs sm:text-sm">✦</span>
        </span>
      ))}
    </motion.div>
  </div>
);

/* ═══════════════════════════════════════════ */
export default function HomePage() {
  const [homeData, setHomeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const imageY = useTransform(scrollYProgress, [0, 1], ['0%', '12%']);

  useEffect(() => {
    productService.getHomePage()
      .then(data => {
        setHomeData(data);
        setLoadError('');
      })
      .catch((error) => {
        setLoadError(error.message || 'Trenutno nije moguce ucitati podatke.');
        toast.error(error.message || 'Trenutno nije moguce ucitati podatke.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  return (
    <div id="pocetna-vrh">

      {/* ══════════════════════════════════════════
          HERO + traka: jedan ekran (~90% / ~10%), slika od xl+
      ══════════════════════════════════════════ */}
      <section
        className="flex h-[calc(100dvh-4rem)] min-h-0 flex-col overflow-hidden md:h-[calc(100dvh-5rem)]"
        aria-label="Uvod"
      >
        <div
          ref={heroRef}
          className="flex min-h-0 flex-[9] basis-0 flex-col overflow-hidden xl:flex-row"
        >
        <div className="relative flex min-h-0 w-full flex-1 flex-col overflow-hidden bg-transparent z-10 xl:w-[44%] xl:flex-none xl:bg-primary">

          {/* Mobile/Tablet hero image (desktop koristi desni panel) */}
          <div
            className="absolute inset-0 xl:hidden bg-no-repeat bg-cover bg-center"
            style={{ backgroundImage: `url('${process.env.PUBLIC_URL}/brand/hero-pasture.png')` }}
          />
          <div className="absolute inset-0 xl:hidden bg-gradient-to-b from-black/10 via-black/25 to-black/35" />

          {/* Grain texture */}
          <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
            style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")` }} />

          {/* Ghost "2021" watermark bottom-right */}
          <div className="absolute bottom-0 right-0 select-none pointer-events-none overflow-hidden leading-none">
            <span className="font-serif font-black text-primary-foreground block"
              style={{ fontSize: 'clamp(120px, 18vw, 240px)', opacity: 0.045, lineHeight: 0.8 }}>
              2021
            </span>
          </div>

          {/* Left gold accent bar */}
          <motion.div
            initial={{ scaleY: 0 }} animate={{ scaleY: 1 }}
            transition={{ delay: 0.1, duration: 1.0, ease: [0.4, 0, 0.2, 1] }}
            style={{ originY: 0 }}
            className="absolute left-0 top-0 bottom-0 w-[3px] bg-gold/55"
          />

          {/* ── CONTENT: u okviru jednog ekrana; na uskim ekranima unutrašnji skrol ── */}
          <div className="relative z-10 flex flex-1 min-h-0 flex-col justify-between overflow-y-auto px-6 sm:px-10 md:px-14 lg:px-16 py-6 sm:py-8 md:py-10">

            {/* ── TOP: eyebrow ── */}
            <motion.div
              initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.5 }}
              className="flex items-center gap-3"
            >
              <div className="h-px w-10 bg-gold/45" />
              <span className="text-gold/70 font-ui text-[11px] tracking-[0.32em] uppercase">
                Fazlić · Sjenica · od 2021.
              </span>
            </motion.div>

            {/* ── MIDDLE: headline + rule + bio + quote ── */}
            <div className="flex-1 flex flex-col justify-center py-4 sm:py-6 min-h-0">
              {/* Headline — fills full width, very large */}
              <motion.h1
                className="font-serif text-primary-foreground leading-[0.95] mb-4 sm:mb-6 md:mb-8"
                initial="hidden"
                animate="visible"
                variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
              >
                {['Ukus koji', 'zauvek', 'pamtite.'].map((line, i) => (
                  <motion.span
                    key={i}
                    variants={{
                      hidden:   { opacity: 0, y: 40 },
                      visible:  { opacity: 1, y: 0, transition: { duration: 0.65, ease: [0.4, 0, 0.2, 1] } }
                    }}
                    className={`block font-bold w-full ${i === 2 ? 'italic text-gold' : 'text-primary-foreground'}`}
                    style={{ fontSize: 'clamp(3.15rem, 13vw, 5.2rem)' }}
                  >
                    {line}
                  </motion.span>
                ))}
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.28, duration: 0.55 }}
                className="font-ui text-primary-foreground/85 text-sm sm:text-base tracking-wide max-w-2xl mb-3 sm:mb-4"
              >
                Domaći i sjenički proizvodi u Prijepolju, sir, kajmak, suvo meso i tradicija. Online poručivanje,
                dostava i lično preuzimanje u radnji Fazlić.
              </motion.p>

              {/* Animated gold divider rule */}
              <motion.div
                initial={{ scaleX: 0 }} animate={{ scaleX: 1 }}
                transition={{ delay: 0.5, duration: 0.9, ease: 'easeOut' }}
                style={{ originX: 0 }}
                className="h-px bg-gradient-to-r from-gold/70 via-gold/35 to-transparent mb-4 sm:mb-6 md:mb-8 w-full max-w-[160px]"
              />

              {/* Bio text — wider, fills the div */}
              <motion.p
                initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.65 }}
                className="font-garamond text-white font-medium leading-[1.75] sm:leading-[1.85] mb-4 sm:mb-6 md:mb-7 max-w-2xl"
                style={{ fontSize: 'clamp(0.9rem, 2.8vw, 1.15rem)', color: '#ffffff' }}
              >
                Sećanja na topli hleb, sveže mleko i miris domaće kuhinje. Negde na Sjeničkoj planini,
                još uvek postoji ukus koji si zaboravio da tražiš. Donosimo ga pravo do tvojih vrata,
                pažljivo upakovano i spremno da stigne do tvoje trpeze.
              </motion.p>

              {/* Family quote */}
              <motion.div
                initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.72, duration: 0.6 }}
                className="border-l-2 border-gold/55 pl-5"
              >
                <p className="font-garamond text-white sm:text-primary-foreground/55 italic font-medium leading-relaxed"
                  style={{ fontSize: 'clamp(1rem, 1.4vw, 1.2rem)' }}>
                  "Donosimo ti ukus koji se više ne nalazi u prodavnici."
                </p>
                <span className="text-white/75 sm:text-primary-foreground/25 text-[11px] font-ui tracking-wider mt-1.5 block">
                  — Porodica Fazlić
                </span>
              </motion.div>
            </div>

            {/* ── BOTTOM: stats + CTA ── */}
            <div className="shrink-0 pt-2">
              {/* CTA */}
              <motion.div
                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.82, duration: 0.5 }}
                className="flex flex-wrap items-center gap-2 sm:gap-3"
              >
                <Link to="/proizvodi">
                  <span
                    className="inline-flex items-center gap-2.5 bg-gold-cta hover:bg-gold
                               text-white font-ui font-semibold tracking-wide
                               px-6 py-3 sm:px-8 sm:py-4 rounded-full shadow-gold
                               transition-all duration-300 cursor-pointer text-sm sm:text-base"
                  >
                    Poručite proizvode
                    <MoveRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
                  </span>
                </Link>
                <Link to="/kontakt">
                  <span
                    className="inline-flex items-center gap-2.5 border border-primary-foreground/30
                               text-primary-foreground/70 hover:border-primary-foreground/60 hover:text-primary-foreground
                               font-ui font-medium tracking-wide
                               px-5 py-3 sm:px-7 sm:py-4 rounded-full
                               transition-all duration-300 cursor-pointer text-sm sm:text-base"
                  >
                    Kontakt
                  </span>
                </Link>
              </motion.div>
            </div>

          </div>
        </div>

        {/* Desni panel — xl+ da tableti (često i u landscape) ostanu bez slike */}
        <div
          className="relative hidden xl:block flex-1 min-h-0 overflow-hidden"
          aria-hidden="true"
        >
          <motion.div
            className="absolute inset-0 bg-no-repeat"
            style={{
              backgroundImage: `url('${process.env.PUBLIC_URL}/brand/hero-pasture.png')`,
              backgroundSize: 'cover',
              backgroundPosition: 'left bottom',
              y: imageY,
            }}
          />
          <div className="absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-primary/25 to-transparent pointer-events-none" />
        </div>
        </div>

        <div
          className="flex min-h-0 flex-[1] basis-0 flex-col border-t border-primary/12"
          aria-label="Proizvodi u ponudi"
        >
          <Marquee />
        </div>
      </section>


      {/* ══════════════════════════════════════════
          BRAND STORY (odmah posle heroa / trake)
      ══════════════════════════════════════════ */}
      <section className="py-16 md:py-24 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="rounded-2xl overflow-hidden shadow-elevated">
                <img
                  src={`${process.env.PUBLIC_URL}/brand/nasa-prica-store.jpg`}
                  alt="Fazlić prodavnica"
                  className="w-full aspect-[4/3] object-cover object-center"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }}
            >
              <div className="divider-ornament mb-4">
                <span className="font-serif text-sm text-gold uppercase tracking-widest">Naša priča</span>
              </div>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-5 leading-snug">
                Porodična Tradicija
                <span className="block text-primary italic">sa Sjeničke Planine</span>
              </h2>
              <div className="space-y-4 text-muted-foreground font-garamond leading-relaxed text-base">
                <p>
                  Sjenički Proizvodi Fazlić su porodična radnja iz Prijepolja posvećena domaćim
                  proizvodima proverenog kvaliteta.
                </p>
                <p>
                  Sarađujemo sa pouzdanim proizvođačima i biramo artikle koje i sami preporučujemo
                  svojoj porodici i prijateljima.
                </p>
                <p>
                  Bilo da poručujete online ili dolazite lično, cilj nam je isti: da dobijete
                  sveže, ukusne i kvalitetne proizvode.
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                {[['100%', 'Domaća proizvodnja'], ['0%', 'Hemije'], ['Poreklo', 'Sjenica']].map(([val, lbl]) => (
                  <div key={lbl} className="flex-1 min-w-28 bg-card rounded-xl p-4 shadow-card border border-border text-center">
                    <div className="font-serif text-xl font-bold text-primary mb-1">{val}</div>
                    <div className="text-xs text-muted-foreground font-ui">{lbl}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>


      {/* ══════════════════════════════════════════
          FEATURED PRODUCTS
      ══════════════════════════════════════════ */}
      <section id="proizvodi" className="py-16 md:py-20 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="text-center mb-12">
              <div className="divider-ornament mb-4">
                <span className="font-serif text-sm text-gold uppercase tracking-widest">Izdvojeni Proizvodi</span>
              </div>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-4">
                Naši Najtraženiji Proizvodi
              </h2>
              <p className="text-muted-foreground text-base font-garamond max-w-xl mx-auto leading-relaxed">
                Izdvojili smo proizvode koje kupci najčešće biraju i kojima se uvek vraćaju.
              </p>
            </motion.div>

            {loading ? (
              <ProductGridSkeleton count={6} />
            ) : loadError ? (
              <div className="rounded-xl border border-border bg-card p-6 text-center">
                <p className="font-ui text-sm text-muted-foreground">{loadError}</p>
              </div>
            ) : (
              <motion.div variants={stagger} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {homeData?.featuredProducts?.map(product => (
                  <motion.div key={product.id} variants={fadeUp}>
                    <ProductCard product={product} />
                  </motion.div>
                ))}
              </motion.div>
            )}

            <motion.div variants={fadeUp} className="text-center mt-10">
              <Link to="/proizvodi">
                <Button variant="outline" size="lg"
                  className="border-primary text-primary hover:bg-primary hover:text-primary-foreground font-ui font-medium transition-all duration-300 rounded-full px-10">
                  Pogledaj sve proizvode
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>


      {/* ══════════════════════════════════════════
          ADVANTAGES
      ══════════════════════════════════════════ */}
      <section className="py-16 md:py-20 bg-gradient-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden" whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
            variants={stagger}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {ADVANTAGES.map(adv => {
              const Icon = adv.icon;
              return (
                <motion.div key={adv.title} variants={fadeUp}
                  className="bg-card rounded-xl p-6 shadow-card text-center flex flex-col items-center">
                  <div className="h-12 w-12 rounded-full bg-accent flex items-center justify-center mb-4">
                    <Icon size={22} className="text-primary" />
                  </div>
                  <h3 className="font-serif text-base font-semibold text-foreground mb-2">{adv.title}</h3>
                  <p className="text-muted-foreground text-sm font-garamond leading-relaxed">{adv.desc}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>


      {/* ══════════════════════════════════════════
          TESTIMONIALS
      ══════════════════════════════════════════ */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="text-center mb-12">
              <div className="divider-ornament mb-4">
                <span className="font-serif text-sm text-gold uppercase tracking-widest">Recenzije</span>
              </div>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground">
                Šta Kažu Naši Kupci
              </h2>
              <div className="mt-4">
                <a
                  href="https://www.google.com/search?q=fazlic+prijepolje&ie=UTF-8#lrd=0x4757f98cabfdd4db:0xac203c2c03fdc875,1,,,,"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-2 text-sm font-ui text-foreground hover:bg-primary/10 transition-colors"
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/brand/google-logo.png`}
                    alt="Google logo"
                    className="h-4 w-4 object-contain"
                  />
                  <span className="font-semibold text-primary">5.0</span>
                  <span>na Google ocenama</span>
                </a>
              </div>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {TESTIMONIALS.map(t => (
                <motion.div key={t.name} variants={fadeUp}
                  className="bg-card rounded-xl p-6 shadow-card border border-border flex flex-col">
                  {t.featured && (
                    <div className="mb-3">
                      <span className="inline-flex items-center gap-1 rounded-full bg-gold/15 px-2.5 py-1 text-[11px] font-ui font-semibold text-primary">
                        <Star size={12} className="text-gold fill-gold" />
                        Izdvojena recenzija
                      </span>
                    </div>
                  )}
                  <Quote size={24} className="text-gold/40 mb-3" />
                  <p className="font-garamond text-foreground/80 leading-relaxed flex-1 mb-5">
                    &ldquo;{t.text}&rdquo;
                  </p>
                  <div className="mt-auto">
                    <div className="flex items-center gap-1 mb-2">
                      {Array.from({ length: t.stars }).map((_, j) => (
                        <Star key={j} size={13} className="text-gold fill-gold" />
                      ))}
                    </div>
                    <p className="font-ui text-sm font-medium text-foreground">{t.name}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>


      {/* ══════════════════════════════════════════
          CTA BANNER
      ══════════════════════════════════════════ */}
      <section className="relative py-20 overflow-hidden" style={{ background: 'hsl(var(--primary))' }}>
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1599150823265-f6100d46e262?w=1200&auto=format&fit=crop&q=60')`
        }} />
        <div className="relative z-10 max-w-3xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.55 }}
          >
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
              Poruči Danas, Uživaj Sutra
            </h2>
            <p className="text-primary-foreground/70 font-garamond text-lg mb-8 leading-relaxed">
              Izaberite domaće proizvode iz naše ponude i poručite brzo i jednostavno.
              Tu smo za sva pitanja i preporuke oko kupovine.
            </p>
            <Link to="/proizvodi">
              <Button size="lg"
                className="bg-gold-cta hover:bg-gold text-white font-ui font-semibold shadow-gold px-10 rounded-full">
                Naruči Odmah
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

    </div>
  );
}
